import { Lightning01 } from '@untitledui/icons/Lightning01';
import { BookOpen01 } from '@untitledui/icons/BookOpen01';
import { Rocket01 } from '@untitledui/icons/Rocket01';
import { CameraLens } from '@untitledui/icons/CameraLens';
import PaymentCard from '../components/PaymentCard';
import { usePaymentQuery, useResumePaymentMutation } from "../api/mutations/payment.query";
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getUserInfo } from '../api/auth';
import { setUser } from '../redux/me.slice';
import { toast } from 'react-toastify';
import Notification from '../components/Notification';
import BackButton from '../components/BackButton';
import LimitsUsageBox from '../components/LimitsUsageBox';
import { openModals } from "../redux/modal.slice";
import ModalsEnum from "../enums/modal.enum";

const Payment = () => {
    const paymentData = usePaymentQuery();
    const user = useSelector((state) => state.me.data);
    const { mutate: resumeSubscription, isPending: isResuming } = useResumePaymentMutation();
    
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const hasReturnedToTab = useRef(false);
    
    // Refetch user data when tab becomes visible and mark that user returned
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const paying = searchParams.get('paying');
                // Only mark as returned if paying=true exists
                if (paying === 'true') {
                    hasReturnedToTab.current = true;
                }
                queryClient.invalidateQueries(['user-info']);
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [queryClient, searchParams]);
    
    useEffect(() => {
        const success = searchParams.get('success');
        const paying = searchParams.get('paying');
        
        if (success === 'true') {
            queryClient.invalidateQueries(['user-info']);
            navigate('/payment', { replace: true });
            return;
        }
        
        // Only redirect if: paying=true AND user returned to tab AND user has subscription
        if (paying === 'true' && hasReturnedToTab.current && user?.user_premium_status) {
            const hasActiveSubscription = user?.user_premium_status?.has_premium_access;
            
            if (hasActiveSubscription) {
                navigate('/', { replace: true });
            }
        }
    }, [searchParams, navigate, queryClient, user]);
    
    const handleBeforePayment = async (paymentId) => {
        try {
            const response = await getUserInfo();
            const freshUserData = response.data;
            
            dispatch(setUser(freshUserData));
            
            const billingInterval = freshUserData?.user_premium_status?.billing?.interval;
            const hasPremium = freshUserData?.user_premium_status?.has_premium_access;
            
            const selectedPlan = paymentData?.data?.data?.find(item => item?.price_id === paymentId);
            const planType = selectedPlan?.price_name;
            
            const hasYearly = billingInterval === 'yearly' && hasPremium;
            const hasMonthly = billingInterval === 'monthly' && hasPremium;
            
            if (hasYearly) {
                toast.info(<Notification text="You already have an active yearly subscription!" icon={'info'} />);
                return false;
            }
            
            if (hasMonthly && planType === 'monthly') {
                toast.info(<Notification text="You already have an active monthly subscription!" icon={'info'} />);
                return false;
            }
            
            return true;
        } catch (error) {
            console.error('Error checking subscription status:', error);
            toast.error(<Notification text="Failed to verify subscription status. Please try again." icon={'warning'} />);
            return false;
        }
    };

    const handleResumeSubscription = () => {
        resumeSubscription(undefined, {
            onSuccess: () => {
                queryClient.invalidateQueries(['user-info']);
                toast.success(<Notification text="Subscription resumed successfully!" icon={'info'} />);
            },
        });
    };

    const openUploadAndGenerateModal = () => {
        dispatch(
            openModals({
                modalIds: [ModalsEnum.CANCEL_SUBSCRIPTION_MODAL],
                modalData: [
                    {
                        className: 'cancel-subscription-modal',
                    }
                ]
            })
        );
    }

    return (
        <div className="payment">
            <div className="container">
                <div className="payment__content flex">
                    <div className="payment__left">
                        <BackButton href="/" className="mb-16" />
                        <h2 onClick={openUploadAndGenerateModal}>Manage Your Subscription</h2>
                        <ul>
                            <li>
                                <div className="icon">
                                    <CameraLens />
                                </div>
                                <div>
                                    <h6>Screenshot & Solve</h6>
                                    <p>Instantly capture and solve quiz questions from any source.</p>
                                </div>
                            </li>
                            <li>
                                <div className="icon">
                                    <Lightning01 />
                                </div>
                                <div>
                                    <h6>In-depth Explanations</h6>
                                    <p>Go beyond answers—understand the “why” behind every solution.</p>
                                </div>
                            </li>
                            <li>
                                <div className="icon">
                                    <BookOpen01 />
                                </div>
                                <div>
                                    <h6>Centralized Study Hub</h6>
                                    <p>Keep all your questions, answers, and quizzes organized in one place.</p>
                                </div>
                            </li>
                            <li>
                                <div className="icon">
                                    <Rocket01 />
                                </div>
                                <div>
                                    <h6>Upload & Generate AI Quizzes</h6>
                                    <p>Transform your notes and materials into personalized practice quizzes.</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div className="payment__right">
                        {user?.user_premium_status?.usage !== null && (
                            <LimitsUsageBox
                                daily_solves_limit={user?.user_premium_status?.usage?.daily_solves_limit}
                                daily_solves_used={user?.user_premium_status?.usage?.daily_solves_used}
                                weekly_quizzes_limit={user?.user_premium_status?.usage?.weekly_quizzes_limit}
                                weekly_quizzes_used={user?.user_premium_status?.usage?.weekly_quizzes_used}
                            />
                        )}
                        {user?.user_premium_status?.billing?.interval !== 'monthly' && user?.user_premium_status?.billing?.interval !== 'yearly' &&
                            <h4 className='upgrade-title'>Upgrade</h4>
                        }
                        <div className={`${user?.user_premium_status?.billing?.interval !== 'monthly' && user?.user_premium_status?.billing?.interval !== 'yearly' ? 'flex payment-box' : 'payed'} mb-24`}>
                            {(() => {
                                const premiumStatus = user?.user_premium_status?.billing?.interval;
                                const items = paymentData?.data?.data || [];
                                
                                // Get monthly and yearly prices for savings calculation
                                const monthlyItem = items.find(i => i?.price_name === 'monthly');
                                const yearlyItem = items.find(i => i?.price_name === 'yearly');
                                const monthlyPrice = monthlyItem?.price_amount || 0;
                                const yearlyPerMonth = yearlyItem ? yearlyItem.price_amount / 12 : 0;
                                const savingsPercent = monthlyPrice > 0 
                                    ? Math.floor(((monthlyPrice - yearlyPerMonth) / monthlyPrice) * 100) 
                                    : 0;
                                
                                // Sort: active plan first
                                const sortedItems = [...items].sort((a, b) => {
                                    if (premiumStatus === 'monthly') {
                                        return a?.price_name === 'monthly' ? -1 : 1;
                                    }
                                    if (premiumStatus === 'yearly') {
                                        return a?.price_name === 'yearly' ? -1 : 1;
                                    }
                                    return 0;
                                });
                                
                                return sortedItems.map((item) => {
                                    const isMonthly = item?.price_name === "monthly";
                                    const isYearly = item?.price_name === "yearly";
                                    
                                    const isActivePlan = premiumStatus === item?.price_name;
                                    
                                    let buttonText = 'Choose Plan';
                                    let disabled = false;
                                    let upgradeLabel = null;
                                    
                                    const subscriptionStatus = user?.user_premium_status?.status;
                                    const isCanceled = !!user?.user_premium_status?.canceled_at;
                                    const isPauseScheduled = !!user?.user_premium_status?.pause_scheduled;
                                    
                                    if (premiumStatus === 'monthly') {
                                        if (isMonthly) {
                                            if (isCanceled) {
                                                buttonText = 'Choose Plan';
                                            } else if (subscriptionStatus === 'paused' || isPauseScheduled) {
                                                buttonText = 'Resume Subscription';
                                            } else {
                                                buttonText = 'Cancel Subscription';
                                            }
                                        } else {
                                            buttonText = 'Upgrade';
                                            upgradeLabel = 'Upgrade';
                                        }
                                    } else if (premiumStatus === 'yearly') {
                                        if (isYearly) {
                                            if (isCanceled) {
                                                buttonText = 'Choose Plan';
                                            } else if (subscriptionStatus === 'paused' || isPauseScheduled) {
                                                buttonText = 'Resume Subscription';
                                            } else {
                                                buttonText = 'Cancel Subscription';
                                            }
                                        } else {
                                            disabled = true;
                                        }
                                    }
                                    
                                    return (
                                        <PaymentCard 
                                            key={item?.price_id}
                                            planText={isMonthly ? 'Monthly Plan' : 'Yearly Plan'}
                                            price={`$ ${isMonthly ? item?.price_amount?.toFixed(2) : (item?.price_amount / 12).toFixed(2)}`}
                                            infoText={isMonthly ? 'Billed mothly.' : `$ ${item?.price_amount} Billed Yearly.`}
                                            nextBillingDate={isActivePlan && user?.user_premium_status?.billing?.renewal_date ? new Date(user.user_premium_status.billing.renewal_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null}
                                            benefitsList={isMonthly ?
                                                [
                                                    {text: 'Perfect if you want short-term access'}
                                                ] :
                                                [
                                                    {text: `Best value — save ~${savingsPercent}% compared to monthly`}
                                                ]
                                            }
                                            popular={isYearly && !isActivePlan}
                                            isActive={isActivePlan}
                                            buttonText={buttonText}
                                            disabled={disabled}
                                            upgradeLabel={upgradeLabel}
                                            paymentId={item?.price_id}
                                            onBeforePayment={handleBeforePayment}
                                            navigate={navigate}
                                            onCancelSubscription={openUploadAndGenerateModal}
                                            onResumeSubscription={handleResumeSubscription}
                                            subscriptionStatus={isActivePlan ? user?.user_premium_status?.status : null}
                                            isCanceled={isActivePlan ? isCanceled : false}
                                            isPauseScheduled={isActivePlan ? !!user?.user_premium_status?.pause_scheduled : false}
                                        />
                                    );
                                });
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Payment;